/**
 * UranIA PA — Admin cutover gate dashboard (US-023, fase 5).
 *
 * Route `/admin/cutover-gates` — pagina interna read-only che traccia
 * gli 8 gate di no-go del cutover Fase 5 → Fase 6 (analisi §25.5 +
 * PRD US-F5-09). La dashboard è alimentata da un JSON committato in
 * `apps/spa/src/lib/admin/cutover-gates-fase-5.json`, validato contro
 * `docs/security/schemas/cutover-gates.schema.json`.
 *
 * AC coperti dal componente (US-023):
 *   - AC#1 — Lista degli 8 gate (per sez. 25.5 dell'analisi):
 *       1. DPIA + FRIA approvate DPO Sicilia
 *       2. Qualifica PSN ottenuta
 *       3. Pentest white-box 0 High aperti
 *       4. Golden replay verde 13 skill; citation coverage > 95%
 *       5. Audit WORM verificato end-to-end
 *       6. SPID L2 in produzione
 *       7. Banner art. 50 su tutte le superfici (smoke)
 *       8. Runbook DR drill superato (RTO 4h dimostrato)
 *   - AC#2 — Per ogni gate: status ✅/⚠️/❌ + evidence link + 4 sign-offs
 *     (DPO + Compliance + Tech Lead + Sponsor).
 *   - AC#3 — Green banner "go-live abilitato" SOLO se tutti gli 8 gate
 *     `ok` AND tutte le 4 firme `signed`.
 *   - AC#4 — Bootstrap Italia 2.x semantico (table caption, scope=col,
 *     role status, alert role) → axe-core 0 violazioni `serious`.
 *   - AC#5 — Typecheck strict (tsc --noEmit).
 *   - AC#6 — Verifica browser via Playwright (axe + smoke).
 *
 * Boundary:
 *   - RBAC guard UX: solo `platform_admin` (replica pattern US-028 /
 *     US-029). L'autorizzazione reale resta server-side via API Gateway
 *     (le route `/admin/...` non sono raggiungibili senza ruolo).
 *   - **Read-only** lato SPA: lo stato dei gate è single source of truth
 *     nel JSON committato (governance-controlled, PR review 2-eyes con
 *     DPO + Tech Lead). Nessuna mutazione client-side.
 *   - Il banner "go-live abilitato" rappresenta una **autorizzazione
 *     tecnica** UI: la decisione operativa di avviare il cutover resta
 *     in capo allo Sponsor (cfr. cap. 25 analisi).
 */

import { useTranslation } from 'react-i18next';
import { createRoute } from '@tanstack/react-router';
import type { JSX } from 'react';

import {
  CUTOVER_GATES_FASE_5,
  countGatesByStatus,
  countSignedSignatures,
  isGoLiveAuthorized,
} from '../../../lib/admin/cutover-gates.ts';
import type {
  CutoverGateDto,
  CutoverGateStatus,
  CutoverGatesDto,
  CutoverSignatureDto,
  CutoverSignaturesDto,
} from '../../../lib/admin/cutover-gates.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../../../lib/auth/rbac.ts';
import { Link } from '@tanstack/react-router';
import { Route as RootRoute } from '../../__root.tsx';

interface GateStatusBadgeProps {
  readonly status: CutoverGateStatus;
}

function GateStatusBadge(props: GateStatusBadgeProps): JSX.Element {
  const { t } = useTranslation();
  const variant: Record<CutoverGateStatus, string> = {
    ok: 'bg-success',
    warning: 'bg-warning text-dark',
    blocked: 'bg-danger',
    pending: 'bg-secondary',
  };
  const symbol: Record<CutoverGateStatus, string> = {
    ok: '✅',
    warning: '⚠️',
    blocked: '❌',
    pending: '⏳',
  };
  const label = t(`admin.cutover_gates.status.${props.status}`);
  const ariaLabel = t('admin.cutover_gates.status_aria_label', { label });
  return (
    <span
      className={`badge ${variant[props.status]}`}
      role="img"
      aria-label={ariaLabel}
      data-testid={`admin-cutover-gate-status-${props.status}`}
    >
      <span aria-hidden="true">{symbol[props.status]}</span> <span>{label}</span>
    </span>
  );
}

interface GateEvidenceLinkProps {
  readonly gate: CutoverGateDto;
}

function GateEvidenceLink(props: GateEvidenceLinkProps): JSX.Element {
  const { t } = useTranslation();
  const { gate } = props;
  if (gate.evidence_url === '') {
    return (
      <span className="text-muted" data-testid={`admin-cutover-gate-evidence-empty-${gate.id}`}>
        {t('admin.cutover_gates.evidence.empty')}
      </span>
    );
  }
  const label =
    gate.evidence_label !== undefined && gate.evidence_label !== ''
      ? gate.evidence_label
      : gate.evidence_url;
  return (
    <a
      href={gate.evidence_url}
      className="link-primary"
      rel="noopener noreferrer"
      data-testid={`admin-cutover-gate-evidence-${gate.id}`}
    >
      <code>{label}</code>
    </a>
  );
}

interface GateRowProps {
  readonly gate: CutoverGateDto;
}

function GateRow(props: GateRowProps): JSX.Element {
  const { t } = useTranslation();
  const { gate } = props;
  return (
    <tr data-testid={`admin-cutover-gate-row-${gate.id}`}>
      <th scope="row">
        <span className="badge bg-primary me-2" aria-label={t('admin.cutover_gates.columns.index_aria', { n: gate.index })}>
          #{gate.index}
        </span>
        <span>{gate.title_it}</span>
      </th>
      <td>
        <GateStatusBadge status={gate.status} />
      </td>
      <td>
        <GateEvidenceLink gate={gate} />
      </td>
      <td>
        <small className="text-muted">{gate.owner}</small>
      </td>
      <td>
        <time dateTime={gate.target_iso_date}>{gate.target_iso_date}</time>
      </td>
    </tr>
  );
}

interface GatesTableProps {
  readonly gates: readonly CutoverGateDto[];
}

function GatesTable(props: GatesTableProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="table-responsive">
      <table
        className="table table-striped"
        data-testid="admin-cutover-gates-table"
      >
        <caption className="visually-hidden">
          {t('admin.cutover_gates.table_caption')}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('admin.cutover_gates.columns.gate')}</th>
            <th scope="col">{t('admin.cutover_gates.columns.status')}</th>
            <th scope="col">{t('admin.cutover_gates.columns.evidence')}</th>
            <th scope="col">{t('admin.cutover_gates.columns.owner')}</th>
            <th scope="col">{t('admin.cutover_gates.columns.target_date')}</th>
          </tr>
        </thead>
        <tbody>
          {props.gates.map((gate) => (
            <GateRow gate={gate} key={gate.id} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface SignatureCellProps {
  readonly signature: CutoverSignatureDto;
  readonly testid: string;
}

function SignatureCell(props: SignatureCellProps): JSX.Element {
  const { t } = useTranslation();
  const { signature, testid } = props;
  if (signature.status === 'signed') {
    return (
      <li className="list-group-item" data-testid={`${testid}-signed`}>
        <div className="d-flex justify-content-between">
          <span>
            <strong>{signature.role}</strong>
            <span className="badge bg-success ms-2" aria-hidden="true">
              ✅
            </span>
            <span className="visually-hidden">
              {t('admin.cutover_gates.signatures.signed_aria', { role: signature.role })}
            </span>
          </span>
          <small className="text-muted">
            {signature.signed_by ?? t('admin.cutover_gates.signatures.unknown_signer')}
          </small>
        </div>
        {signature.signed_at !== undefined && (
          <small className="text-muted">
            <time dateTime={signature.signed_at}>{signature.signed_at}</time>
          </small>
        )}
      </li>
    );
  }
  return (
    <li className="list-group-item" data-testid={`${testid}-pending`}>
      <div className="d-flex justify-content-between">
        <span>
          <strong>{signature.role}</strong>
          <span className="badge bg-secondary ms-2" aria-hidden="true">
            ⏳
          </span>
          <span className="visually-hidden">
            {t('admin.cutover_gates.signatures.pending_aria', { role: signature.role })}
          </span>
        </span>
        <small className="text-muted">{t('admin.cutover_gates.signatures.pending')}</small>
      </div>
    </li>
  );
}

interface SignatureBlockProps {
  readonly signatures: CutoverSignaturesDto;
}

function SignatureBlock(props: SignatureBlockProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <section
      className="urania-cutover-gates-signatures mt-5"
      aria-labelledby="admin-cutover-gates-signatures-heading"
    >
      <h2 id="admin-cutover-gates-signatures-heading" className="h4">
        {t('admin.cutover_gates.signatures.title')}
      </h2>
      <p className="text-muted small">
        {t('admin.cutover_gates.signatures.subtitle')}
      </p>
      <ul className="list-group" data-testid="admin-cutover-gates-signatures">
        <SignatureCell
          signature={props.signatures.dpo}
          testid="admin-cutover-gates-signature-dpo"
        />
        <SignatureCell
          signature={props.signatures.compliance_officer}
          testid="admin-cutover-gates-signature-compliance"
        />
        <SignatureCell
          signature={props.signatures.tech_lead}
          testid="admin-cutover-gates-signature-tech-lead"
        />
        <SignatureCell
          signature={props.signatures.sponsor}
          testid="admin-cutover-gates-signature-sponsor"
        />
      </ul>
    </section>
  );
}

interface GoLiveBannerProps {
  readonly data: CutoverGatesDto;
}

function GoLiveBanner(props: GoLiveBannerProps): JSX.Element {
  const { t } = useTranslation();
  const authorized = isGoLiveAuthorized(props.data);
  const counters = countGatesByStatus(props.data);
  const signedCount = countSignedSignatures(props.data);

  if (authorized) {
    return (
      <div
        className="alert alert-success"
        role="status"
        aria-live="polite"
        data-testid="admin-cutover-gates-banner-authorized"
      >
        <strong>{t('admin.cutover_gates.banner.authorized.title')}</strong>
        <p className="mb-0">{t('admin.cutover_gates.banner.authorized.body')}</p>
      </div>
    );
  }
  const gatesRemaining = 8 - counters.ok;
  const signaturesRemaining = 4 - signedCount;
  return (
    <div
      className="alert alert-warning"
      role="status"
      aria-live="polite"
      data-testid="admin-cutover-gates-banner-blocked"
    >
      <strong>{t('admin.cutover_gates.banner.blocked.title')}</strong>
      <p className="mb-0">
        {t('admin.cutover_gates.banner.blocked.body', {
          gates_remaining: gatesRemaining,
          signatures_remaining: signaturesRemaining,
        })}
      </p>
    </div>
  );
}

function CutoverGatesSummary(props: { readonly data: CutoverGatesDto }): JSX.Element {
  const { t } = useTranslation();
  const counters = countGatesByStatus(props.data);
  const signedCount = countSignedSignatures(props.data);
  return (
    <dl className="row mb-4" data-testid="admin-cutover-gates-summary">
      <dt className="col-sm-3">{t('admin.cutover_gates.summary.phase')}</dt>
      <dd className="col-sm-9">
        <code>{props.data.phase}</code>
      </dd>
      <dt className="col-sm-3">{t('admin.cutover_gates.summary.branch')}</dt>
      <dd className="col-sm-9">
        <code>{props.data.branch}</code>
      </dd>
      <dt className="col-sm-3">{t('admin.cutover_gates.summary.compiled_at')}</dt>
      <dd className="col-sm-9">
        <time dateTime={props.data.compiled_at}>{props.data.compiled_at}</time>
        <span className="text-muted ms-2">— {props.data.compiled_by}</span>
      </dd>
      <dt className="col-sm-3">{t('admin.cutover_gates.summary.gates')}</dt>
      <dd className="col-sm-9">
        {t('admin.cutover_gates.summary.gates_breakdown', {
          ok: counters.ok,
          warning: counters.warning,
          blocked: counters.blocked,
          pending: counters.pending,
        })}
      </dd>
      <dt className="col-sm-3">{t('admin.cutover_gates.summary.signatures')}</dt>
      <dd className="col-sm-9">
        {t('admin.cutover_gates.summary.signatures_breakdown', {
          signed: signedCount,
        })}
      </dd>
    </dl>
  );
}

export function AdminCutoverGatesPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);

  if (!isAuthenticated) {
    return (
      <section
        className="urania-admin-cutover-gates"
        data-testid="admin-cutover-gates-unauthenticated"
      >
        <h1 className="display-5 mb-3">{t('admin.cutover_gates.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('admin.cutover_gates.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('admin.cutover_gates.unauthenticated.message')}</p>
          <Link
            to="/login"
            className="btn btn-primary"
            data-testid="admin-cutover-gates-go-to-login"
          >
            {t('admin.cutover_gates.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <section
        className="urania-admin-cutover-gates"
        data-testid="admin-cutover-gates-forbidden"
      >
        <h1 className="display-5 mb-3">{t('admin.cutover_gates.title')}</h1>
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="admin-cutover-gates-forbidden-alert"
        >
          <strong>{t('admin.cutover_gates.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.cutover_gates.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section
      className="urania-admin-cutover-gates"
      data-testid="admin-cutover-gates-page"
    >
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
      </nav>
      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="position-relative" style={{ zIndex: 1, maxWidth: 760 }}>
          <span
            className="badge mb-2"
            style={{
              backgroundColor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.7rem',
              letterSpacing: '0.05em',
            }}
          >
            ADMIN · CUTOVER GATES
          </span>
          <h1 className="h3 mb-2">{t('admin.cutover_gates.title')}</h1>
          <p className="mb-0 opacity-90 small">{t('admin.cutover_gates.subtitle')}</p>
        </div>
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-30px', right: '-30px', opacity: 0.12 }}
          width="200"
          height="200"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M5.338 1.59a61 61 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.7 10.7 0 0 0 2.287 2.233c.346.244.652.42.893.533q.18.085.293.118a1 1 0 0 0 .101.025 1 1 0 0 0 .1-.025q.114-.034.294-.118c.24-.113.547-.29.893-.533a10.7 10.7 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524z" />
          <path d="M10.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708 0l-1.5-1.5a.5.5 0 1 1 .708-.708L7.5 7.793l2.646-2.647a.5.5 0 0 1 .708 0z" />
        </svg>
      </div>

      <GoLiveBanner data={CUTOVER_GATES_FASE_5} />
      <CutoverGatesSummary data={CUTOVER_GATES_FASE_5} />

      <h2 className="h4 mb-3" id="admin-cutover-gates-heading">
        {t('admin.cutover_gates.gates_heading')}
      </h2>
      <GatesTable gates={CUTOVER_GATES_FASE_5.gates} />

      <SignatureBlock signatures={CUTOVER_GATES_FASE_5.signatures} />
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/cutover-gates',
  component: AdminCutoverGatesPage,
});
